import {
  AlfaRomeo,
  Audi,
  BMW,
  Citroen,
  Cupra,
  Dacia,
  Ds,
  Fiat,
  Ford,
  Hyundai,
  Jeep,
  Kia,
  Lexus,
  Maserati,
  Mercedes,
  Mini,
  Nissan,
  Opel,
  Peugeot,
  Polestar,
  Porsche,
  Renault,
  Seat,
  Skoda,
  Tesla,
  Toyota,
  Vauxhall,
  Volkswagen,
  Volvo,
} from '@highmobility/console-ui-components';

import americanExpress from '@/assets/images/stripe/americanExpress.png';
import cartesBancaires from '@/assets/images/stripe/cartesBancaires.png';
import dinersClub from '@/assets/images/stripe/dinersClub.png';
import discover from '@/assets/images/stripe/discover.png';
import jcb from '@/assets/images/stripe/jcb.png';
import masterCard from '@/assets/images/stripe/mastercard.png';
import unionPay from '@/assets/images/stripe/unionpay.png';
import visa from '@/assets/images/stripe/visa.png';

import { Brand, BrandData, Oem, ProjectStatus, ProjectType, Region, WebhookEvent } from '@/types';

export const PUBLIC_PATH = process.env.CONSOLE_UI_PUBLIC_PATH || '';

export const SIMULATORS_PATH =
  process.env.CONSOLE_UI_SIMULATORS_PATH || 'https://console.develop.high-mobility.net/simulators';

export const API_PATH = `${PUBLIC_PATH.replace(/\/$/, '')}/api/v1`;

export const FAKE_LOADER_TIMEOUT = 500;

export const AUTH_REDIRECT_PARAM = 'redirect';

export const MAX_PENDING_INVITES = 10;

export const PROJECT_REGION: Record<Region, string> = {
  europe: 'Europe',
  north_america: 'North America',
};

export const PROJECT_STATUS: Record<ProjectStatus, string> = {
  draft: 'Draft',
  in_review: 'Review',
  published: 'Published',
  published_with_changes: 'Published with changes',
};

export const PROJECT_TYPE: Record<ProjectType, string> = {
  fleet: 'Fleet',
  non_fleet: 'Non-fleet',
};

export const OEMS: Record<Oem, { name: string; brands: Brand[] }> = {
  bmw: {
    name: 'BMW Group',
    brands: ['bmw', 'mini'],
  },
  mercedes_benz: {
    name: 'Mercedes-Benz',
    brands: ['mercedes_benz'],
  },
  kia: {
    name: 'Kia',
    brands: ['kia'],
  },
  toyota: {
    name: 'Toyota Motor Corporation',
    brands: ['toyota', 'lexus'],
  },
  renault: {
    name: 'Renault Group',
    brands: ['renault', 'dacia'],
  },
  ford: {
    name: 'Ford',
    brands: ['ford'],
  },
  porsche: {
    name: 'Porsche',
    brands: ['porsche'],
  },
  stellantis: {
    name: 'Stellantis',
    brands: [
      'peugeot',
      'citroen',
      'opel',
      'vauxhall',
      'ds',
      'fiat',
      'alfaromeo',
      'jeep',
      'maserati',
    ],
  },
  tesla: {
    name: 'Tesla',
    brands: ['tesla'],
  },
  volkswagen: {
    name: 'Volkswagen Group',
    brands: ['cupra', 'volkswagen', 'seat', 'skoda', 'audi'],
  },
  volvo_cars: {
    name: 'Volvo Cars',
    brands: ['volvo_cars'],
  },
  polestar: {
    name: 'Polestar',
    brands: ['polestar'],
  },
  nissan: {
    name: 'Nissan',
    brands: ['nissan'],
  },
  hyundai: {
    name: 'Hyundai',
    brands: ['hyundai'],
  },
};

export const BRANDS: Record<Brand, BrandData> = {
  ford: {
    id: 'ford',
    name: 'Ford',
    logo: Ford,
  },
  mercedes_benz: {
    id: 'mercedes_benz',
    name: 'Mercedes-Benz',
    logo: Mercedes,
  },
  bmw: {
    id: 'bmw',
    name: 'BMW',
    logo: BMW,
  },
  mini: {
    id: 'mini',
    name: 'MINI',
    logo: Mini,
  },
  peugeot: {
    id: 'peugeot',
    name: 'Peugeot',
    logo: Peugeot,
  },
  citroen: {
    id: 'citroen',
    name: 'Citroën',
    logo: Citroen,
  },
  ds: {
    id: 'ds',
    name: 'DS Automobiles',
    logo: Ds,
  },
  fiat: {
    id: 'fiat',
    name: 'Fiat',
    logo: Fiat,
  },
  alfaromeo: {
    id: 'alfaromeo',
    name: 'Alfa Romeo',
    logo: AlfaRomeo,
  },
  jeep: {
    id: 'jeep',
    name: 'Jeep',
    logo: Jeep,
  },
  maserati: {
    id: 'maserati',
    name: 'Maserati',
    logo: Maserati,
  },
  porsche: {
    id: 'porsche',
    name: 'Porsche',
    logo: Porsche,
  },
  renault: {
    id: 'renault',
    name: 'Renault',
    logo: Renault,
  },
  dacia: {
    id: 'dacia',
    name: 'Dacia',
    logo: Dacia,
  },
  kia: {
    id: 'kia',
    name: 'Kia',
    logo: Kia,
  },
  lexus: {
    id: 'lexus',
    name: 'Lexus',
    logo: Lexus,
  },
  toyota: {
    id: 'toyota',
    name: 'Toyota',
    logo: Toyota,
  },
  tesla: {
    id: 'tesla',
    name: 'Tesla',
    logo: Tesla,
  },
  opel: {
    id: 'opel',
    name: 'Opel',
    logo: Opel,
  },
  vauxhall: {
    id: 'vauxhall',
    name: 'Vauxhall',
    logo: Vauxhall,
  },
  audi: {
    id: 'audi',
    name: 'Audi',
    logo: Audi,
  },
  hyundai: {
    id: 'hyundai',
    name: 'Hyundai',
    logo: Hyundai,
  },
  volvo_cars: {
    id: 'volvo_cars',
    name: 'Volvo',
    logo: Volvo,
  },
  cupra: {
    id: 'cupra',
    name: 'Cupra',
    logo: Cupra,
  },
  seat: {
    id: 'seat',
    name: 'SEAT',
    logo: Seat,
  },
  skoda: {
    id: 'skoda',
    name: 'Skoda',
    logo: Skoda,
  },
  volkswagen: {
    id: 'volkswagen',
    name: 'Volkswagen',
    logo: Volkswagen,
  },
  polestar: {
    id: 'polestar',
    name: 'Polestar',
    logo: Polestar,
  },
  nissan: {
    id: 'nissan',
    name: 'Nissan',
    logo: Nissan,
  },
};

export const ISO_ALPHA2_EUROPE_COUNTRIES = {
  AL: 'Albania',
  AD: 'Andorra',
  AT: 'Austria',
  BY: 'Belarus',
  BE: 'Belgium',
  BA: 'Bosnia And Herzegovina',
  BG: 'Bulgaria',
  HR: 'Croatia',
  CY: 'Cyprus',
  CZ: 'Czech Republic',
  DK: 'Denmark',
  EE: 'Estonia',
  FI: 'Finland',
  FR: 'France',
  DE: 'Germany',
  GR: 'Greece',
  HU: 'Hungary',
  IS: 'Iceland',
  IE: 'Ireland',
  IT: 'Italy',
  LV: 'Latvia',
  LI: 'Liechtenstein',
  LT: 'Lithuania',
  LU: 'Luxembourg',
  MT: 'Malta',
  MD: 'Moldova',
  MC: 'Monaco',
  ME: 'Montenegro',
  NL: 'Netherlands',
  NO: 'Norway',
  PL: 'Poland',
  PT: 'Portugal',
  RO: 'Romania',
  RU: 'Russian Federation',
  SM: 'San Marino',
  RS: 'Serbia',
  SK: 'Slovakia',
  SI: 'Slovenia',
  ES: 'Spain',
  SE: 'Sweden',
  CH: 'Switzerland',
  TR: 'Turkey',
  UA: 'Ukraine',
  GB: 'United Kingdom',
};

export const STRIPE_CARD_BRANDS: Record<
  string,
  { id: string; name: string; logo: string | null } | undefined
> = {
  amex: {
    id: 'amex',
    name: 'American Express',
    logo: americanExpress,
  },
  cartes_bancaires: {
    id: 'cartes_bancaires',
    name: 'Cartes Bancaires',
    logo: cartesBancaires,
  },
  diners: {
    id: 'diners',
    name: 'Diners Club',
    logo: dinersClub,
  },
  discover: {
    id: 'discover',
    name: 'Discover',
    logo: discover,
  },
  jcb: {
    id: 'jcb',
    name: 'JCB',
    logo: jcb,
  },
  mastercard: {
    id: 'mastercard',
    name: 'Mastercard',
    logo: masterCard,
  },
  visa: {
    id: 'visa',
    name: 'Visa',
    logo: visa,
  },
  unionpay: {
    id: 'unionpay',
    name: 'UnionPay',
    logo: unionPay,
  },
};

export const MFAMethods = {
  TOTP: 'totp',
  RECOVERY_CODE: 'recovery_code',
} as const;

export const WEBHOOK_EVENTS: Record<WebhookEvent, WebhookEvent> = {
  fleet_clearance_changed: 'fleet_clearance_changed',
  fleet_geofence_notification: 'fleet_geofence_notification',
};

//mapping is specified here: https://docs.google.com/spreadsheets/d/1twujrSfR6pgC3H9eOmY0DXDNAJvOY3gfHYQp20wom78/edit?gid=0#gid=0
export const CLEARANCE_ERROR_MAPPINGS: Partial<Record<Oem, Record<string, string>>> = {
  bmw: {
    UNSPECIFIED: 'Unspecified',
    INVALID_CONFIG: 'Invalid client configuration',
    INVALID_VIN: 'Invalid VIN',
    VEHICLE_IN_ANOTHER_HM_FLEET: 'VIN is assigned to a different app or project',
    VEHICLE_IN_ANOTHER_OEM_FLEET: 'VIN is assigned to a different OEM fleet',
  },
  mercedes_benz: {
    UNSPECIFIED: 'Unspecified',
    CONTROL_MEASURE_FAILED: 'Control measure validation failed',
    CONTROL_MEASURE_TIMEOUT: 'Control measure validation timeout',
    INVALID_SERVICE_IDS: 'Unspecified',
    INVALID_VIN: 'Invalid VIN',
    SERVICE_ACTIVATION_TIMEOUT: 'VIN activation timeout',
    ODOMETER_SERVICE_UNAVAILABLE: 'Invalid client configuration',
  },
  ford: {
    UNSPECIFIED: 'Unspecified',
    INVALID_SKU: 'Invalid client configuration',
    INVALID_VIN: 'Invalid VIN',
    NO_CONSENT: 'Consent assignment missing',
    PRODUCT_CONFLICT: 'Conflicting package already active',
    UNENROLLMENT_IN_PROGRESS: 'Invalid OEM state: VIN revoke in progress',
  },
  kia: {
    UNSPECIFIED: 'Unspecified',
    INELIGIBLE: 'Ineligible VIN',
    INVALID_CONFIG: 'Invalid client configuration',
    NOT_ALLOWED: 'VIN activation not allowed by OEM',
    VEHICLE_NOT_ACTIVATED: 'In-vehicle connectivity settings are disabled',
  },
  porsche: {
    UNSPECIFIED: 'Unspecified',
    INVALID_CONFIG: 'Invalid client configuration',
  },
  stellantis: {
    UNSPECIFIED: 'Unspecified',
    INVALID_PACKAGE: 'Invalid client configuration',
    INELIGIBLE: 'Ineligible VIN',
    TELEMATIC_BOX_ERROR: 'Telematics hardware error',
    INVALID_VIN: 'Invalid VIN',
    ACTIVATED_WITH_DIFFERENT_PACKAGE: 'VIN is assigned to a different app or project',
    CONTRACT_REJECTED: 'OEM fleet assignment failed',
    CONTRACT_FAILED: 'OEM fleet assignment failed',
    CONTRACT_ACTIVATING: 'Activation in progress on OEM side',
  },
  tesla: {
    UNSPECIFIED: 'Unspecified',
    INVALID_CONFIG: 'Invalid client configuration',
    NO_CONSENT: 'Consent assignment missing',
    UNKNOWN_VEHICLE: 'Consent assignment missing',
  },
  renault: {
    UNSPECIFIED: 'Unspecified',
    INELIGIBLE: 'Ineligible VIN',
    INVALID_CONTRACT: 'Invalid OEM state',
    UNABLE_TO_BE_ADDED_TO_FLEET: 'OEM fleet assignment failed',
    CONTRACT_ALREADY_ACTIVATED: 'OEM fleet assignment failed',
  },
  toyota: {
    UNSPECIFIED: 'Unspecified',
    INVALID_CONFIG: 'Invalid client configuration',
    INVALID_VIN: 'Invalid VIN',
    INELIGIBLE: 'Ineligible VIN',
  },
  volkswagen: {
    UNSPECIFIED: 'Unspecified',
    ENROLLMENT_FAILED: 'Unspecified',
    VIN_CHECK_FAILED: 'Ineligible or constrained VIN',
    NOT_CONFIRMED: 'Unspecified',
    MISSING_CUSTOMER_NAME: 'Customer name tag missing',
    VEHICLE_IN_ANOTHER_HM_FLEET: 'VIN is assigned to a different app or project',
    INVALID_VIN: 'Invalid VIN',
    PRIVACY_MODE_IS_ENABLED: 'Privacy mode is enabled',
    INELIGIBLE: 'Ineligible VIN',
    UNABLE_TO_BE_ADDED_TO_FLEET: 'OEM fleet assignment failed',
    ERROR_MOD_VALIDATION: 'Vehicle platform not supported',
    PRIMARY_USER_ENROLLED: 'Primary user enrolled',
    WRONG_SOFTWARE_VERSION: 'Software version incompatible',
    NO_DATA: 'Activation timeout: no data',
  },
  volvo_cars: {
    UNSPECIFIED: 'Unspecified',
    INVALID_CONFIG: 'Invalid client configuration',
    DATA_NOT_FOUND: 'Activation timeout: no data',
    NOT_ACTIVATED: 'Unspecified',
    VEHICLE_NOT_IN_FLEET: 'OEM fleet assignment failed',
    INELIGIBLE: 'Ineligible VIN',
  },
  polestar: {
    UNSPECIFIED: 'Unspecified',
    INVALID_CONFIG: 'Invalid client configuration',
    NOT_ACTIVATED: 'OEM fleet assignment failed',
    IN_ANOTHER_HM_FLEET: 'VIN is assigned to a different app or project ',
  },
  nissan: {
    UNSPECIFIED: 'Unspecified',
    INELIGIBLE: 'Ineligible VIN',
  },
};
